import type { ProjectOverview, ProjectVerdict } from '../types/project';

interface ProjectSwitcherProps {
  projects: ProjectOverview[];
  activeProjectId: string;
  onSelect: (projectId: string) => void;
}

/** Status dot color per verdict - the at-a-glance health of each project. */
const SWITCHER_DOT: Record<ProjectVerdict, string> = {
  'on-track': 'bg-emerald-500',
  'at-risk': 'bg-rose-500',
  'off-track': 'bg-rose-600',
};

/**
 * Navigation between projects - a row of pills, each carrying the project's name
 * and a status dot, so a manager can scan the portfolio's health and switch
 * focus in one click.
 */
export function ProjectSwitcher({
  projects,
  activeProjectId,
  onSelect,
}: ProjectSwitcherProps) {
  return (
    <div className="flex items-center gap-2 overflow-x-auto pb-1">
      <span className="me-1 shrink-0 text-[11px] font-semibold uppercase tracking-[0.16em] text-zinc-400">
        Projects
      </span>
      {projects.map((project) => {
        const isActive = project.id === activeProjectId;
        return (
          <button
            key={project.id}
            type="button"
            onClick={() => onSelect(project.id)}
            className={`flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm transition-all ${
              isActive
                ? 'border-transparent bg-white font-medium text-zinc-900 shadow-[0_10px_28px_-14px_rgba(16,27,58,0.55)] ring-1 ring-zinc-200'
                : 'border-zinc-200/70 bg-transparent text-zinc-500 hover:bg-white hover:text-zinc-800'
            }`}
          >
            <span
              className={`h-2 w-2 rounded-full ${
                SWITCHER_DOT[project.verdict]
              }`}
            />
            {project.name}
          </button>
        );
      })}
    </div>
  );
}
